import i18n from "../i18n";

/**
 * Normalize Tezam pavilion / booth labels for visitor UI.
 * Upstream often stores full names like "Павильон №1 Зал №3" in `hall`,
 * while `booth` (PlaceOneStand) may still be empty before floor placement.
 */

export function formatHallLabel(hall: string | null | undefined): string {
  const h = (hall ?? "").trim().replace(/\s+/g, " ");
  if (!h) return "";
  // Already a human-readable pavilion/hall name from Tezam — do not prefix again.
  if (/павильон|pavilion|зал\b|hall\b/i.test(h)) return h;
  // Strip accidental "Пав." / "Pav." prefix before re-formatting.
  const stripped = h.replace(/^(пав\.?|pav\.?)\s+/i, "").trim();
  if (/павильон|pavilion|зал\b|hall\b/i.test(stripped)) return stripped;
  return i18n.t("location.pavilion", { value: stripped || h });
}

export function formatBoothLabel(booth: string | null | undefined): string | null {
  const b = (booth ?? "").trim().replace(/\s+/g, " ");
  if (!b) return null;
  if (/^стенд\b/i.test(b)) return b;
  return i18n.t("location.booth", { value: b });
}

/** Single line for cards: "Стенд A12 · Павильон №1" or just one part if the other is missing. */
export function formatCompanyLocation(input: {
  hall?: string | null;
  booth?: string | null;
  /** When every exhibitor shares one hall, hide it — booth is the useful locator. */
  hideHall?: boolean;
}): string {
  const booth = formatBoothLabel(input.booth);
  const hall = input.hideHall ? "" : formatHallLabel(input.hall);
  if (booth && hall) return `${booth} · ${hall}`;
  return booth || hall || "";
}

/** True when hall labels collapse to a single shared value (or empty). */
export function isSharedHall(
  companies: Array<{ hall?: string | null }>,
): boolean {
  const labels = new Set(
    companies.map((c) => (c.hall ?? "").trim()).filter(Boolean),
  );
  return labels.size <= 1;
}
